//src/pages/CadasroClientes.js
import React, { useState, useEffect } from "react";
import axios from "../axiosConfig";
import { useNavigate, Link } from "react-router-dom";
import bgImage from "../assets/bg-login.jpg";
import logo from "../assets/tikpontos.png";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import {
  AlertTriangle,
  Building2,
  CheckCircle2,
  ShieldCheck,
  UserRound,
} from "lucide-react";

import "./CadastroCliente.css";
import AvisoCookies from "../components/AvisoCookies";

import {
  validarSenhaFrontend,
  calcularForcaSenha
} from "../utils/senhaUtils";

function CadastroCliente() {
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [formData, setFormData] = useState({
    nome: "",
    email: "",
    senha: "",
    confirmarSenha: "",
    tipo_pessoa: "fisica",
    documento: "",
    cnpj_empresa: "",
    telefone: "",
    cidade: "",
    uf: "",
    cep_empresa: "",

    tipo_identificador_pj: "",
    identificador_conta_pj: "",
    responsavel_nome: "",
    responsavel_cpf: "",
  });

  const [mensagem, setMensagem] = useState("");
  const [erro, setErro] = useState("");
  const [empresaEncontrada, setEmpresaEncontrada] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const forcaSenha = calcularForcaSenha(formData.senha);

  useEffect(() => {
    const cnpjLimpo = limparDocumento(formData.cnpj_empresa);

    if (cnpjLimpo.length === 14) {
      axios
        .get(`/empresas/cnpj/${cnpjLimpo}`)
        .then((res) => {
          const empresa = res.data;

          setEmpresaEncontrada(empresa);
          setFormData((prev) => ({
            ...prev,
            cidade: empresa.cidade || "",
            uf: empresa.estado || "",
            cep_empresa: empresa.cep || "",
          }));
        })
        .catch(() => {
          setEmpresaEncontrada(null);
          setFormData((prev) => ({
            ...prev,
            cidade: "",
            uf: "",
            cep_empresa: "",
          }));
        });
    } else {
      setEmpresaEncontrada(null);
      setFormData((prev) => ({
        ...prev,
        cidade: "",
        uf: "",
        cep_empresa: "",
      }));
    }
  }, [formData.cnpj_empresa]);

  const limparDocumento = (valor) => String(valor || "").replace(/\D/g, "");

  const formatarDocumento = (valor, tipoPessoa) => {
    const numeros = limparDocumento(valor);

    if (tipoPessoa === "fisica") {
      return numeros
        .replace(/^(\d{3})(\d)/, "$1.$2")
        .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
        .replace(/\.(\d{3})(\d)/, ".$1-$2")
        .slice(0, 14);
    }

    return numeros
      .replace(/^(\d{2})(\d)/, "$1.$2")
      .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1/$2")
      .replace(/(\d{4})(\d)/, "$1-$2")
      .slice(0, 18);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "tipo_pessoa") {
      setFormData((prev) => ({
        ...prev,
        tipo_pessoa: value,
        documento: "",
        tipo_identificador_pj: "",
        identificador_conta_pj: "",
        responsavel_nome: "",
        responsavel_cpf: "",
      }));
      return;
    }

    if (name === "documento") {
      setFormData((prev) => ({
        ...prev,
        documento: formatarDocumento(value, prev.tipo_pessoa),
      }));
      return;
    }

    if (name === "cnpj_empresa") {
      setFormData((prev) => ({
        ...prev,
        cnpj_empresa: formatarDocumento(value, "juridica"),
      }));
      return;
    }

    if (name === "responsavel_cpf") {
      setFormData((prev) => ({
        ...prev,
        responsavel_cpf: formatarDocumento(value, "fisica"),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validarDocumentoBasico = () => {
    const doc = limparDocumento(formData.documento);

    if (formData.tipo_pessoa === "fisica") return doc.length === 11;
    if (formData.tipo_pessoa === "juridica") return doc.length === 14;

    return false;
  };

  const validarCNPJEmpresa = () => {
    return limparDocumento(formData.cnpj_empresa).length === 14;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setErro("");
    setMensagem("");

    if (formData.senha !== formData.confirmarSenha) {
      return setErro("As senhas não conferem.");
    }

    const validacaoSenha = validarSenhaFrontend(formData.senha);

    if (!validacaoSenha.valida) {
      return setErro(validacaoSenha.mensagem);
    }

    if (!validarDocumentoBasico()) {
      return setErro(
        formData.tipo_pessoa === "fisica"
          ? "Informe um CPF válido."
          : "Informe um CNPJ válido."
      );
    }

    if (!validarCNPJEmpresa()) {
      return setErro("Informe o CNPJ da empresa participante.");
    }

    if (!empresaEncontrada) {
      return setErro(
        "CNPJ da empresa não encontrado. A empresa precisa estar cadastrada no programa."
      );
    }

    if (formData.tipo_pessoa === "juridica") {
      if (!formData.tipo_identificador_pj || !formData.identificador_conta_pj) {
        return setErro(
          "Para conta PJ, informe se a conta representa empresa, setor ou colaborador."
        );
      }

      if (
        !formData.responsavel_nome ||
        limparDocumento(formData.responsavel_cpf).length !== 11
      ) {
        return setErro("Informe o responsável e o CPF do responsável.");
      }
    }

    try {
      if (!executeRecaptcha) {
        return setErro("reCAPTCHA não carregado.");
      }

      setSalvando(true);

      const recaptchaToken = await executeRecaptcha("cadastro_cliente");

      const payload = {
        nome: formData.nome,
        email: formData.email,
        senha: formData.senha,
        tipo: "cliente",
        tipo_pessoa: formData.tipo_pessoa,
        cpf_cnpj: limparDocumento(formData.documento),
        cnpj_empresa: limparDocumento(formData.cnpj_empresa),

        telefone: formData.telefone,
        cidade: formData.cidade,
        estado: formData.uf.toUpperCase(),
        status: "ativo",

        tipo_identificador_pj:
          formData.tipo_pessoa === "juridica"
            ? formData.tipo_identificador_pj
            : null,

        identificador_conta_pj:
          formData.tipo_pessoa === "juridica"
            ? formData.identificador_conta_pj
            : null,

        responsavel_nome:
          formData.tipo_pessoa === "juridica"
            ? formData.responsavel_nome
            : null,

        responsavel_cpf:
          formData.tipo_pessoa === "juridica"
            ? limparDocumento(formData.responsavel_cpf)
            : null,

        recaptchaToken,
      };

      await axios.post("/clientes", payload);

      setMensagem("Cadastro realizado com sucesso! Redirecionando para o login...");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      console.error("Erro ao cadastrar cliente:", err);

      const msg = err.response?.data?.error || err.response?.data?.erro;

      if (msg) {
        setErro(msg);
      } else {
        setErro("Erro ao cadastrar. Verifique os dados e tente novamente.");
      }
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="cadastroCliente-page">
      <div
        className="cadastroCliente-hero"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <div className="cadastroCliente-heroOverlay" />

        <div className="cadastroCliente-brand">
          <img src={logo} alt="Tikpontos" />
        </div>

        <div className="cadastroCliente-heroContent">
          <span>Programa TikPontos</span>
          <h1>Crie sua conta e comece a pontuar.</h1>
          <p>
            Cadastre-se com segurança, vincule sua conta à empresa participante
            e acompanhe seus pontos no painel.
          </p>
        </div>
      </div>

      <div className="cadastroCliente-formArea">
        <form onSubmit={handleSubmit} className="cadastroCliente-card">
          <div className="cadastroCliente-cardTop" />

          <div className="cadastroCliente-cardBody">
            <div className="cadastroCliente-header">
              <h2>Cadastro de Cliente</h2>
              <p>Preencha os dados para criar sua conta no TikPontos.</p>
            </div>

            <div className="cadastroCliente-tipoConta">
              <button
                type="button"
                className={formData.tipo_pessoa === "fisica" ? "ativo" : ""}
                onClick={() =>
                  handleChange({
                    target: { name: "tipo_pessoa", value: "fisica" },
                  })
                }
              >
                <UserRound size={15} />
                Pessoa Física
              </button>

              <button
                type="button"
                className={formData.tipo_pessoa === "juridica" ? "ativo" : ""}
                onClick={() =>
                  handleChange({
                    target: { name: "tipo_pessoa", value: "juridica" },
                  })
                }
              >
                <Building2 size={15} />
                Pessoa Jurídica
              </button>
            </div>

            <div className="cadastroCliente-infoBox">
              <ShieldCheck size={16} />
              <span>
                Os pontos são vinculados ao titular da conta e não poderão ser
                transferidos após o crédito.
              </span>
            </div>

            <div className="cadastroCliente-grid">
              <div className="cadastroCliente-field wide">
                <label>
                  {formData.tipo_pessoa === "fisica"
                    ? "Nome completo"
                    : "Razão social / nome da conta"}
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="cadastroCliente-field">
                <label>E-mail</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="cadastroCliente-field">
                <label>Telefone</label>
                <input
                  type="tel"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="cadastroCliente-field">
                <label>
                  {formData.tipo_pessoa === "fisica" ? "CPF" : "CNPJ"}
                </label>
                <input
                  type="text"
                  name="documento"
                  value={formData.documento}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="cadastroCliente-field">
                <label>CNPJ da empresa participante</label>
                <input
                  type="text"
                  name="cnpj_empresa"
                  value={formData.cnpj_empresa}
                  onChange={handleChange}
                  required
                />
              </div>

              {formData.cnpj_empresa.replace(/\D/g, "").length === 14 && (
                <div className="cadastroCliente-field wide">
                  {empresaEncontrada ? (
                    <div className="cadastroCliente-empresaOk">
                      <CheckCircle2 size={16} />
                      <div>
                        <strong>{empresaEncontrada.razao_social}</strong>
                        <span>
                          {[formData.cidade, formData.uf]
                            .filter(Boolean)
                            .join(" / ")}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="cadastroCliente-empresaErro">
                      <AlertTriangle size={16} />
                      CNPJ da empresa não encontrado no programa.
                    </div>
                  )}
                </div>
              )}

              {formData.tipo_pessoa === "juridica" && (
                <>
                  <div className="cadastroCliente-infoBox wide">
                    <Building2 size={16} />
                    <span>
                      Contas PJ podem representar a empresa, um setor ou um
                      colaborador autorizado. Informe um identificador claro
                      para evitar dúvidas futuras.
                    </span>
                  </div>

                  <div className="cadastroCliente-field">
                    <label>Esta conta representa</label>
                    <select
                      name="tipo_identificador_pj"
                      value={formData.tipo_identificador_pj}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Selecione</option>
                      <option value="empresa">A empresa como um todo</option>
                      <option value="departamento">Um setor/departamento</option>
                      <option value="colaborador">Um colaborador específico</option>
                    </select>
                  </div>

                  <div className="cadastroCliente-field">
                    <label>
                      {formData.tipo_identificador_pj === "departamento"
                        ? "Nome do setor/departamento"
                        : formData.tipo_identificador_pj === "colaborador"
                        ? "Nome do colaborador"
                        : "Identificador da conta"}
                    </label>
                    <input
                      type="text"
                      name="identificador_conta_pj"
                      value={formData.identificador_conta_pj}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="cadastroCliente-field">
                    <label>Responsável pela conta</label>
                    <input
                      type="text"
                      name="responsavel_nome"
                      value={formData.responsavel_nome}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="cadastroCliente-field">
                    <label>CPF do responsável</label>
                    <input
                      type="text"
                      name="responsavel_cpf"
                      value={formData.responsavel_cpf}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </>
              )}

              <div className="cadastroCliente-field">
                <label>Senha</label>

                <input
                  type="password"
                  name="senha"
                  autoComplete="new-password"
                  value={formData.senha}
                  onChange={handleChange}
                  minLength={8}
                  required
                />

                <small className="cadastroCliente-passwordHint">
                  Use pelo menos 8 caracteres, incluindo letras e números.
                </small>

                {formData.senha && (
                  <div
                    className={`cadastroCliente-passwordStrength ${forcaSenha.nivel}`}
                  >
                    Força da senha: <strong>{forcaSenha.texto}</strong>
                  </div>
                )}
              </div>

              <div className="cadastroCliente-field">
                <label>Confirmar senha</label>
                <input
                    type="password"
                    name="confirmarSenha"
                    autoComplete="new-password"
                    value={formData.confirmarSenha}
                    onChange={handleChange}
                    minLength={8}
                    required
                  />
              </div>
            </div>

            <button type="submit" className="cadastroCliente-submit" disabled={salvando}>
              {salvando ? "Cadastrando..." : "Criar conta"}
            </button>

            {mensagem && (
              <div className="cadastroCliente-message sucesso">{mensagem}</div>
            )}

            {erro && <div className="cadastroCliente-message erro">{erro}</div>}

            <p className="cadastroCliente-loginLink">
              Já tem conta? <Link to="/login">Fazer login</Link>
            </p>
          </div>
        </form>
      </div>

      <AvisoCookies />
    </div>
  );
}

const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function CadastroClienteWrapper() {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={siteKey}
      scriptProps={{ async: true, defer: true }}
    >
      <CadastroCliente />
    </GoogleReCaptchaProvider>
  );
}